import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { VotingPoll, VotingPollStatus } from '../../types/voting';
import { GraphQLVotingService } from '../../services/graphqlVoting';
import { useAuth } from '../../contexts/AuthContext';
import { canCreateVotingPoll } from '../../utils/nominationPermissions';
import { useNominationRole } from '../../hooks/useNominationRole';
import RightSidePanel from '../performance/RightSidePanel';
import CreateVotingPoll from './CreateVotingPoll';
import InlineLoader from '../common/InlineLoader';
import PageHeader from '../common/PageHeader';
import './VotingPolls.css';

const VotingPolls: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { nominationRole } = useNominationRole(user);
  const isCreator = canCreateVotingPoll(nominationRole);

  const [polls, setPolls] = useState<VotingPoll[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'my-polls' | 'vote'>(isCreator ? 'my-polls' : 'vote');
  const [showCreatePanel, setShowCreatePanel] = useState(false);

  const loadPolls = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      if (activeTab === 'my-polls' && isCreator) {
        const data = await GraphQLVotingService.getMyPolls(user.email);
        setPolls(data.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
      } else {
        const data = await GraphQLVotingService.getPollsForVoter(user.email);
        setPolls(data.sort((a, b) => b.createdAt.localeCompare(a.createdAt)));
      }
    } catch (e) {
      console.error('Failed to load polls:', e);
    }
    setLoading(false);
  }, [user, activeTab, isCreator]);

  useEffect(() => { loadPolls(); }, [loadPolls]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this voting poll?')) return;
    const result = await GraphQLVotingService.deletePoll(id);
    if (result.success) loadPolls();
  };

  const handleClose = async (id: string) => {
    if (!window.confirm('Close this poll? No more votes will be accepted.')) return;
    const result = await GraphQLVotingService.closePoll(id);
    if (result.success) loadPolls();
  };

  return (
    <div className="vp-page">
      <PageHeader
        icon={<span style={{ fontSize: '1.5rem' }}>🗳️</span>}
        title="Voting Polls"
        subtitle={isCreator ? 'Create polls and view results' : 'Cast your votes'}
        gradient="linear-gradient(135deg, #7c3aed, #6d28d9)"
        accentColor="#7c3aed"
        actions={isCreator && (
          <button className="vp-create-btn" onClick={() => setShowCreatePanel(true)}>+ New Poll</button>
        )}
      />

      {isCreator && (
        <div className="vp-tabs">
          <button className={`vp-tab ${activeTab === 'my-polls' ? 'active' : ''}`} onClick={() => setActiveTab('my-polls')}>
            My Polls
          </button>
          <button className={`vp-tab ${activeTab === 'vote' ? 'active' : ''}`} onClick={() => setActiveTab('vote')}>
            Vote
          </button>
        </div>
      )}

      {loading ? (
        <InlineLoader message="Loading polls" />
      ) : polls.length === 0 ? (
        <div className="vp-empty">
          <div style={{ fontSize: '3rem' }}>🗳️</div>
          <h3>{activeTab === 'my-polls' ? 'No polls created yet' : 'No polls to vote on'}</h3>
          <p>{activeTab === 'my-polls' ? 'Create your first voting poll' : 'You\'ll be notified when a poll needs your vote'}</p>
        </div>
      ) : (
        <div className="vp-list">
          {polls.map(poll => (
            <div key={poll.id} className="vp-card" onClick={() => navigate(`/voting/${poll.id}`)}>
              <div className="vp-card-header">
                <div className="vp-card-left">
                  <div className="vp-card-icon">🗳️</div>
                  <div>
                    <h3 className="vp-card-title">{poll.title}</h3>
                    {poll.description && <p className="vp-card-desc">{poll.description}</p>}
                  </div>
                </div>
                <span className={`vp-status ${poll.status.toLowerCase()}`}>{poll.status}</span>
              </div>
              <div className="vp-card-meta">
                <span>👥 {poll.nomineeNames.length} nominees</span>
                <span>🗳️ {poll.voterNames.length} voters</span>
                {poll.deadline && <span>⏰ {new Date(poll.deadline).toLocaleDateString()}</span>}
                <span>📅 {new Date(poll.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="vp-card-nominees">
                <span className="vp-card-nominees-label">Nominees:</span>
                {poll.nomineeNames.map((name, i) => (
                  <span key={i} className="vp-nominee-chip">{name}</span>
                ))}
              </div>
              {activeTab === 'my-polls' && isCreator && (
                <div className="vp-card-actions" onClick={e => e.stopPropagation()}>
                  {poll.status === VotingPollStatus.ACTIVE && (
                    <button className="vp-btn-close" onClick={() => handleClose(poll.id)}>Close Poll</button>
                  )}
                  <button className="vp-btn-delete" onClick={() => handleDelete(poll.id)}>Delete</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <RightSidePanel isOpen={showCreatePanel} title="New Voting Poll" onClose={() => setShowCreatePanel(false)} wide>
        <CreateVotingPoll onComplete={() => { setShowCreatePanel(false); loadPolls(); }} embedded />
      </RightSidePanel>
    </div>
  );
};

export default VotingPolls;
